import { useRef, useState } from 'react';
import { useAppDispatch } from 'app/hooks';
import {
  useCreateDmountMutation,
  usePatchDmountMutation,
  useDeleteDmountMutation,
} from 'app/services/ahuBoardFormsApi';
import type { IAhuBoardSection, IAhuBoardTunnel, IAhuDmountRow, ICmsAhuDoc } from 'models';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import {
  useDebouncedCallback,
  validateNumberField,
  formatDecimalDisplay,
} from '../../forms/formUtils';
import { cmsDmounts, cmsDmountPlanes } from '../../forms/cmsDoc';
import {
  validateOnSplitDmountChange,
  validateOnSplitDmountDestroy,
} from '../../validation/validationController';
import ItemsPager from './ItemsPager';
import { DeleteConfirmPopup } from '../SharedPopups';

/**
 * Phase 6e1 — Split D-Mount group (§7.4 / §7.4.2, tab.section.dmount.tmpl
 * markup faithful). + Add POSTs `{section_id, dist_from:0}`, opens the
 * subtab, selects the new chip, validates, queues lug recalc (hidden lugs).
 * Fields clamp inline per CMS ups/downs clearances (vertical) or 0–600
 * (horizontal); horizontal default dist_from = tunnel.height/2.
 *
 * [D5-patch] honored: `update_dmount_dimension` is a DEAD event — canvas
 * dmount dims refresh via the dmounts-collection listeners; the React port
 * refreshes the dmounts cache (canvas reads it through the bootstrap rows)
 * and emits `change:tunnel:dimensions` for the re-render.
 */

interface DmountGroupProps {
  section: IAhuBoardSection;
  tunnel: IAhuBoardTunnel | undefined;
  cms: ICmsAhuDoc | undefined;
  dmounts: IAhuDmountRow[];
  refetchDmounts: () => Promise<unknown> | void;
  open: boolean;
  onToggle: () => void;
  cid: string;
}

const DmountGroup = ({
  section,
  tunnel,
  cms,
  dmounts,
  refetchDmounts,
  open,
  onToggle,
  cid,
}: DmountGroupProps) => {
  const dispatch = useAppDispatch();
  const [createDmount] = useCreateDmountMutation();
  const [patchDmount] = usePatchDmountMutation();
  const [deleteDmount] = useDeleteDmountMutation();
  const [activeId, setActiveId] = useState<number | null>(null);
  const distFromRef = useRef<HTMLInputElement | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<IAhuDmountRow | null>(null);

  const dmountCms = cmsDmounts(cms);
  const planes = cmsDmountPlanes(cms);

  const ids = dmounts.map((d) => d.id);
  const currentId = activeId != null && ids.includes(activeId) ? activeId : ids[0] ?? null;

  const recalcLugs = () =>
    ahuBoardBus.emit('tunnelLugs:recalcPlacement', {
      tunnel_id: section.tunnel_id,
      hide_from_canvas: true,
    });

  const refreshCanvas = () =>
    ahuBoardBus.emit('change:tunnel:dimensions', {
      tunnel_id: section.tunnel_id,
      render: true,
      only_for: section.id,
    });

  /** Refetch, then redraw — the canvas reads the dmount rows out of that
   * query's cache (`selectDmountsBySection`), so redrawing first draws the
   * dimension where the split no longer is. No extra request: it is the same
   * refetch, awaited. */
  const refetchThenRefreshCanvas = () =>
    Promise.resolve(refetchDmounts()).then(refreshCanvas, refreshCanvas);

  /**
   * The row the server stored, back into the From Upstream Edge / From Floor
   * field of the split it belongs to.
   *
   * `dist_from` is not always what was sent. Switching the plane to Vertical
   * re-reads the stored height as a distance from the section's upstream face
   * and `AHUSectionDMount.save()` pushes it clear of the tunnel's base rail,
   * so a PATCH of `{plane_id}` alone comes back with a different number. The
   * input is uncontrolled -- `defaultValue` only lands on mount -- so without
   * this the board showed the old value until the page was reloaded
   * (Shushanik, 2026-10-22).
   *
   * Only ever its own split, and the check has to read the split on screen
   * **when the reply lands**: pressing a pager chip blurs the field, which
   * saves, and the click then switches the form over before the reply
   * arrives. Writing it then would show one split's distance under another's
   * tag -- and the next blur would save it there. `currentId` from this
   * closure is no use for that (it is the id that was on screen when the save
   * went out, i.e. always the saved one), so the input carries its own id and
   * answers for itself.
   */
  const syncDistFromField = (saved: IAhuDmountRow | undefined) => {
    const input = distFromRef.current;
    if (!input || !saved || saved.dist_from == null) return;
    if (input.dataset.dmountId !== String(saved.id)) return;
    // Never over the user's hands: they may be typing the next value already.
    if (document.activeElement === input) return;
    const text = formatDecimalDisplay(saved.dist_from);
    if (input.value !== text) input.value = text;
  };

  const addDmount = () => {
    createDmount({ section_id: section.id, dist_from: 0 })
      .unwrap()
      .then((created) => {
        if (!open) onToggle();
        setActiveId(created.id);
        void dispatch(validateOnSplitDmountChange(created.id));
        recalcLugs();
        void refetchThenRefreshCanvas();
      })
      .catch(() => undefined);
  };

  /** One PATCH per user action. Switching to Horizontal changes the plane AND
   * the distance, and sending those as two overlapping saves let the older
   * reply land last -- each carries the whole row, so the `{plane_id}` one
   * still held the distance from before. */
  const saveFields = (dmount: IAhuDmountRow, data: Record<string, unknown>) => {
    patchDmount({ id: dmount.id, data })
      .unwrap()
      .then((saved) => {
        syncDistFromField(saved);
        if ('plane_id' in data || 'dist_from' in data) {
          void dispatch(validateOnSplitDmountChange(dmount.id));
        }
        void refetchThenRefreshCanvas();
      })
      .catch(() => {
        // §19.2 — dmount save failure restores previous values silently
        // (the only rollback in the codebase): refetch restores the row.
        refetchDmounts();
      });
  };

  const changeField = (dmount: IAhuDmountRow, name: string, value: unknown) =>
    saveFields(dmount, { [name]: value });

  const debouncedChange = useDebouncedCallback(
    (dmount: IAhuDmountRow, name: string, value: unknown) => changeField(dmount, name, value),
    1000
  );

  // QA5 #10a — the CMS gate must come AFTER every hook (it used to sit above
  // useDebouncedCallback: when split_dmount_offered flipped as the CMS doc
  // settled, the hook count changed → React error #310 killed the board).
  if (!dmountCms.split_dmount_offered) return null;

  const confirmDelete = () => {
    const dmount = deleteTarget;
    if (!dmount) return;
    deleteDmount(dmount.id)
      .unwrap()
      .then(() => {
        void dispatch(validateOnSplitDmountDestroy(section.id, dmount.id));
        ahuBoardBus.emit(`dmount:destroy:section:${section.id}`);
        recalcLugs();
        void refetchThenRefreshCanvas();
      })
      .catch(() => undefined)
      .finally(() => setDeleteTarget(null));
  };

  // The Product Class's in-section clearances bound a D-mount in an ordinary
  // section only; a split plenum's belongs to the Split Plenum record. This
  // MIRRORS `AHUSection.class_dmount_clearances_apply` (ksdm#2290) off the same
  // flag, because the section serializer does not carry the rule itself: if the
  // server ever exempts anything else, this has to follow or the box offers
  // bounds the PATCH will refuse. Applying them
  // to a split plenum put the box's own min above its own max -- 4.00" into a
  // 6.00" section asking 4.00" back off the downstream edge -- and since the
  // clamp takes min then max, every value typed went out as 2.00 and came back
  // 400, which `changeField` swallows: the number snapped back with no message.
  const sectionLength = Number(section.length ?? 0);
  const upsClearance = section.split
    ? 0
    : Number(dmountCms.split_dmount_min_in_section_ups_clearance) || 0;
  const downsClearance = section.split
    ? 0
    : Number(dmountCms.split_dmount_min_in_section_downs_clearance) || 0;
  const distMin = upsClearance;
  const distMax = sectionLength - downsClearance;

  const planeName = (planeId: number | null | undefined) => {
    const row = planes.find((p) => p.id === planeId);
    return String(row?.value ?? row?.name ?? '').toLowerCase();
  };

  const active = dmounts.find((d) => d.id === currentId) ?? null;

  return (
    <ul className="inputs-group d-mount" id="inputs-group_d-mount">
      <button
        className=" ka-has-tooltip add-mount btn btn-add f-size-11"
        id="add-dmount"
        onClick={(e) => {
          e.preventDefault();
          addDmount();
        }}
      >
        <span>+</span> Add
      </button>
      <li className="sublist-group">
        <input
          id={`d_mount_${cid}`}
          type="checkbox"
          data-subtab="d_mount"
          name="ahu_left_bar"
          checked={open}
          onChange={onToggle}
          className="sublist-ctrl-checkbox section_subtab"
        />
        <label htmlFor={`d_mount_${cid}`} className={dmounts.length ? '' : 'non-clickable'}>
          Split D-Mount <i className="icon-add" />
        </label>
        <div className="accessory-wrapper" id="dmount_wrapper">
          <ItemsPager
            ids={ids}
            activeId={currentId}
            onSelect={setActiveId}
            className="d_mount_count items-wrapper"
          />
          {open && active && (
            <form
              id={`forms_dmount_${active.id}`}
              onSubmit={(e) => e.preventDefault()}
            >
              <ul className="sublist">
                <li className="sub-heading">
                  <div>
                    <input
                      type="text"
                      className="model_field medium-plus-input"
                      name="tag"
                      defaultValue={String(active.tag ?? '')}
                      maxLength={30}
                      onBlur={(e) => changeField(active, 'tag', e.currentTarget.value)}
                      onInput={(e) => debouncedChange(active, 'tag', e.currentTarget.value)}
                    />{' '}
                    <a
                      data-id={active.id}
                      className="d-mount icon-trash dmount_remove ka-has-tooltip"
                      {...{ tooltip: 'Delete Split D-mount' }}
                      onClick={() => setDeleteTarget(active)}
                    />{' '}
                    <span className="label">
                      <span>Tag</span>
                    </span>
                  </div>
                </li>
                <li>
                  <div>
                    <span className="selection">
                      {planes
                        .filter((p) => p.id === active.plane_id)
                        .map((p) => (
                          <span key={p.id}>{String(p.name ?? '')}</span>
                        ))}
                    </span>{' '}
                    <select
                      className="model_field medium-plus-input"
                      name="plane_id"
                      value={active.plane_id ?? ''}
                      onChange={(e) => {
                        const planeId = Number(e.currentTarget.value);
                        const changes: Record<string, unknown> = { plane_id: planeId };
                        // horizontal with no dist_from → tunnel.height/2 (§7.4.2).
                        if (
                          planeName(planeId) === 'horizontal' &&
                          (active.dist_from == null || Number(active.dist_from) === 0)
                        ) {
                          changes.dist_from = Number(tunnel?.height ?? 0) / 2;
                        }
                        saveFields(active, changes);
                      }}
                    >
                      {planes.map((p) => (
                        <option key={p.id} value={p.id}>
                          {String(p.name ?? '')}
                        </option>
                      ))}
                    </select>{' '}
                    <span className="label">
                      <span>Plane</span>
                    </span>
                  </div>
                </li>
                {planeName(active.plane_id) === 'vertical' ? (
                  <li>
                    <div>
                      <span className="unit">in</span>{' '}
                      <input
                        type="text"
                        data-type="number"
                        data-min={distMin}
                        data-step="0.01"
                        data-max={distMax}
                        className="text-left model_field large-input"
                        name="dist_from"
                        ref={distFromRef}
                        // A fresh input per split, so one split's text can
                        // never be left standing under another's tag. NOT
                        // keyed on the value as well: the refetch would then
                        // remount the field under someone who is typing in
                        // it. Value repaints go through `syncDistFromField`,
                        // which leaves a focused field alone.
                        key={active.id}
                        data-dmount-id={active.id}
                        defaultValue={formatDecimalDisplay(active.dist_from)}
                        onBlur={(e) => {
                          const res = validateNumberField(e.currentTarget.value, {
                            min: distMin,
                            max: distMax,
                            mode: 'clamp',
                            step: 0.01,
                          });
                          if (res.ok && res.value != null) {
                            e.currentTarget.value = formatDecimalDisplay(res.value);
                            changeField(active, 'dist_from', res.value);
                          }
                        }}
                      />{' '}
                      <span className="label">
                        <span>From Upstream Edge </span>
                      </span>
                    </div>
                  </li>
                ) : (
                  <li>
                    <div>
                      <span className="unit">in</span>{' '}
                      <input
                        type="text"
                        data-type="number"
                        data-min="0"
                        data-step="0.01"
                        data-max="600.00"
                        className="text-left model_field large-input"
                        name="dist_from"
                        ref={distFromRef}
                        // A fresh input per split, so one split's text can
                        // never be left standing under another's tag. NOT
                        // keyed on the value as well: the refetch would then
                        // remount the field under someone who is typing in
                        // it. Value repaints go through `syncDistFromField`,
                        // which leaves a focused field alone.
                        key={active.id}
                        data-dmount-id={active.id}
                        defaultValue={formatDecimalDisplay(active.dist_from)}
                        onBlur={(e) => {
                          const res = validateNumberField(e.currentTarget.value, {
                            min: 0,
                            max: 600,
                            mode: 'clamp',
                            step: 0.01,
                          });
                          if (res.ok && res.value != null) {
                            e.currentTarget.value = formatDecimalDisplay(res.value);
                            changeField(active, 'dist_from', res.value);
                          }
                        }}
                      />{' '}
                      <span className="label">
                        <span>From Floor </span>
                      </span>
                    </div>
                  </li>
                )}
              </ul>
            </form>
          )}
        </div>
      </li>
      {deleteTarget && (
        <DeleteConfirmPopup
          title="Delete D-Mount?"
          content="If you choose to Delete, all D-Mount Data will be lost and will not be recoverable."
          name={deleteTarget.tag ?? ''}
          onDelete={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </ul>
  );
};

export default DmountGroup;
